import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Mail, MailOpen, Reply, Trash2, Send, Loader2, Clock, MessageSquarePlus, Pencil, Plus, X } from "lucide-react";

const fmt = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
};

const TemplateManager = ({ templates, saveTemplate, deleteTemplate }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {template_id?, title, body}
  const [saving, setSaving] = useState(false);

  const startNew = () => setEditing({ title: "", body: "" });
  const submit = async () => {
    if (!editing?.title.trim() || !editing?.body.trim()) return toast.error("Title and body are required.");
    setSaving(true);
    const ok = await saveTemplate(editing);
    setSaving(false);
    if (ok) setEditing(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="manage-templates-btn" className="rounded-full border-black/20">
          <MessageSquarePlus className="mr-1.5 h-4 w-4" /> Manage templates
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="templates-dialog">
        <DialogHeader><DialogTitle>Reply templates</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Create, edit and delete canned replies for contact messages.</DialogDescription>

        {editing ? (
          <div className="space-y-3" data-testid="template-editor">
            <div>
              <label className="text-xs text-muted-foreground">Title</label>
              <Input data-testid="template-title-input" value={editing.title}
                onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Deal added" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Message</label>
              <Textarea data-testid="template-body-input" value={editing.body} rows={6}
                onChange={(e) => setEditing((p) => ({ ...p, body: e.target.value }))}
                placeholder="Type the canned reply…" className="mt-1" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" data-testid="template-cancel-btn" onClick={() => setEditing(null)}>Cancel</Button>
              <Button data-testid="template-save-btn" onClick={submit} disabled={saving} className="rounded-full">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save template
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="max-h-72 space-y-2 overflow-y-auto" data-testid="templates-manager-list">
              {templates.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No templates yet.</p>
              ) : templates.map((t) => (
                <div key={t.template_id} data-testid={`template-row-${t.template_id}`}
                  className="flex items-start gap-2 rounded-lg border border-black/10 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm">{t.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                  </div>
                  <button data-testid={`template-edit-${t.template_id}`} onClick={() => setEditing({ ...t })}
                    className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`template-delete-${t.template_id}`}
                    onClick={() => window.confirm(`Delete "${t.title}"?`) && deleteTemplate(t.template_id)}
                    className="text-destructive hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <Button variant="outline" data-testid="template-new-btn" onClick={startNew} className="w-full rounded-full border-dashed">
              <Plus className="mr-1.5 h-4 w-4" /> New template
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ContactTab = ({ messages, reload, templates = [], saveTemplate, deleteTemplate }) => {
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReply(""); }, [openId]);

  const openMsg = async (m) => {
    setOpenId(openId === m.msg_id ? null : m.msg_id);
    if (!m.read) {
      try { await api.post(`/admin/contact-messages/${m.msg_id}/read`); reload(); }
      catch (e) { console.error("Failed to mark message read", e); }
    }
  };

  const sendReply = async (m) => {
    if (!reply.trim()) return toast.error("Write a reply first.");
    setBusy(true);
    try {
      await api.post(`/admin/contact-messages/${m.msg_id}/reply`, { reply });
      toast.success(`Reply sent to ${m.email}`);
      setReply("");
      setOpenId(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the reply");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete the message from ${m.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/contact-messages/${m.msg_id}`);
      toast.success("Message deleted");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  const headerBar = (
    <div className="mb-4 flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {messages.filter((m) => !m.read).length} unread · {messages.length} total
      </p>
      <TemplateManager templates={templates} saveTemplate={saveTemplate} deleteTemplate={deleteTemplate} />
    </div>
  );

  if (!messages?.length) {
    return (
      <div>
        {headerBar}
        <div data-testid="contact-empty" className="rounded-xl border border-dashed border-black/15 bg-white p-10 text-center text-muted-foreground">
          No contact messages yet. Messages from the landing page's contact form land here.
        </div>
      </div>
    );
  }

  return (
    <div>
      {headerBar}
      <div className="space-y-3" data-testid="contact-messages-list">
      {messages.map((m) => {
        const isOpen = openId === m.msg_id;
        return (
          <div key={m.msg_id} data-testid={`contact-msg-${m.msg_id}`}
            className={`rounded-xl border bg-white transition-colors ${m.read ? "border-black/10" : "border-primary/40"}`}>
            <button onClick={() => openMsg(m)} data-testid={`contact-open-${m.msg_id}`}
              className="flex w-full items-center gap-3 p-4 text-left">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${m.read ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                {m.read ? <MailOpen className="h-4.5 w-4.5" /> : <Mail className="h-4.5 w-4.5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold truncate">{m.name}</p>
                  {!m.read && <Badge data-testid={`contact-unread-${m.msg_id}`} className="bg-primary/15 text-primary border-none">New</Badge>}
                  {m.replied_at && <Badge className="bg-green-100 text-green-800 border-none">Replied</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{m.email} · {fmt(m.created_at)}</p>
                {!isOpen && <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{m.message}</p>}
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-black/5 p-4">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.message}</p>
                {m.replied_at && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-green-700">
                    <Clock className="h-3.5 w-3.5" /> Replied {fmt(m.replied_at)}
                  </p>
                )}
                <div className="mt-3">
                  {templates.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`template-chips-${m.msg_id}`}>
                      <span className="text-xs text-muted-foreground self-center mr-0.5">Quick reply:</span>
                      {templates.map((t) => (
                        <button key={t.template_id} type="button"
                          data-testid={`template-chip-${t.template_id}`}
                          onClick={() => setReply(t.body)}
                          className="rounded-full border border-black/15 bg-secondary/50 px-2.5 py-1 text-xs font-medium hover:bg-secondary transition-colors duration-150">
                          {t.title}
                        </button>
                      ))}
                    </div>
                  )}
                  <Textarea data-testid={`contact-reply-input-${m.msg_id}`} value={reply}
                    onChange={(e) => setReply(e.target.value)} rows={4}
                    placeholder={`Reply to ${m.name}…`} />
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" data-testid={`contact-send-reply-${m.msg_id}`} onClick={() => sendReply(m)}
                      disabled={busy} className="rounded-full">
                      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                      Send reply
                    </Button>
                    <a href={`mailto:${m.email}`} data-testid={`contact-mailto-${m.msg_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-black/20 px-3 py-1.5 text-sm hover:bg-secondary">
                      <Reply className="h-3.5 w-3.5" /> Open in email app
                    </a>
                    <Button size="sm" variant="ghost" data-testid={`contact-delete-${m.msg_id}`} onClick={() => remove(m)}
                      className="ml-auto text-destructive hover:bg-destructive/10">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default ContactTab;
